import React, { useId } from 'react';
import './Field.css';

/**
 * Tennis Diary UI Kit — Field
 *
 *   <Field label="Email" required hint="We never share it">
 *     <Input type="email" />
 *   </Field>
 *
 *   <Field label="About" error="Required">
 *     <Textarea />
 *   </Field>
 *
 * Field clones its single child to attach `id`/`aria-describedby`/`aria-invalid`.
 * Works with native <input>/<textarea>/<select> AND the Input/Textarea/Select
 * primitives below.
 */
export const Field = ({
  label,
  required = false,
  hint,
  error,
  htmlFor,
  children,
  className = '',
}) => {
  const reactId = useId();
  const id = htmlFor || `tdk-field-${reactId}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined;

  const child = React.Children.only(children);
  const childProps = {
    id,
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : undefined,
    invalid: error ? true : child.props.invalid,
  };

  return (
    <label htmlFor={id} className={`tdk-field ${className}`}>
      {label && (
        <span className="tdk-field__label">
          {label}
          {required && <span className="tdk-field__required" aria-hidden="true">*</span>}
        </span>
      )}
      {React.cloneElement(child, childProps)}
      {hint && !error && (
        <span id={hintId} className="tdk-field__hint">{hint}</span>
      )}
      {error && (
        <span id={errId} className="tdk-field__error" role="alert">{error}</span>
      )}
    </label>
  );
};

const buildInputClassName = (extra, invalid) =>
  ['tdk-input', invalid ? 'tdk-input--invalid' : '', extra].filter(Boolean).join(' ');

export const Input = React.forwardRef(function Input(
  { className = '', invalid = false, ...rest },
  ref,
) {
  return <input ref={ref} className={buildInputClassName(className, invalid)} {...rest} />;
});

export const Textarea = React.forwardRef(function Textarea(
  { className = '', invalid = false, rows = 4, ...rest },
  ref,
) {
  return <textarea ref={ref} rows={rows} className={buildInputClassName(className, invalid)} {...rest} />;
});

export const Select = React.forwardRef(function Select(
  { className = '', invalid = false, children, ...rest },
  ref,
) {
  return (
    <select ref={ref} className={buildInputClassName(className, invalid)} {...rest}>
      {children}
    </select>
  );
});
